/* STENCIL IMPORTS */
import { h, Host, } from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { detectClickOutside } from "../../../common/detect-click-outside";
import { KeyboardKeys as KK } from "../../../common/types";
export class GxgMenuSlim {
    constructor() {
        this.menuTitle = undefined;
        this.tabs = undefined;
        this.hidden = true;
        this.hideOnSelect = true;
        this.ellipsis = true;
    }
    /*
  INDEX:
  1.OWN PROPERTIES
  2.REFERENCE TO ELEMENTS
  3.STATE() VARIABLES
  4.PUBLIC PROPERTY API | WATCH'S
  5.EVENTS (EMIT)
  6.COMPONENT LIFECYCLE EVENTS
  7.LISTENERS
  8.PUBLIC METHODS API
  9.LOCAL METHODS
  10.RENDER() FUNCTION
  */
    // 1.OWN PROPERTIES //
    _menuHeight = "0px";
    /**
     * The delay in milliseconds that the menu will remain visible after mouseout event fires.
     */
    _mouseOutDelay = 1000;
    /**
     * It holds a reference to the 'mouseOutTimeout' timeout set using the setTimeout function
     */
    mouseOutTimeout;
    /**
     * The menu height transition timing.
     */
    _transitionDuration = 200;
    /**
     * The enabled (not disabled) menu items
     */
    _enabledItems = [];
    // 2.REFERENCE TO ELEMENTS //
    el;
    innerWrapper;
    hiddenHandler(hidden) {
        if (hidden) {
            this._menuHeight = "0px";
            this.el.setAttribute("tabindex", "-1");
            this.removeDetectClickOutside();
            this.removeMouseOut();
            this.removeMouseEnter();
        }
        else {
            this._menuHeight = this.innerWrapper.offsetHeight + "px";
            this.el.removeAttribute("tabindex");
            setTimeout(() => {
                this.setFocusFirstItem();
                this.attachDetectClickOutside();
                this.attachMouseOut();
                this.attachMouseEnter();
            }, this._transitionDuration);
        }
    }
    // 5.EVENTS (EMIT) //
    // 6.COMPONENT LIFECYCLE EVENTS //
    componentWillLoad() {
        this.assignEllipsis();
        /*Prevent this.mouseOutTimeout firing on page load*/
        clearTimeout(this.mouseOutTimeout);
        this.getEnabledItems();
    }
    disconnectedCallback() {
        this.removeDetectClickOutside();
        this.removeMouseOut();
        this.removeMouseEnter();
    }
    // 7.LISTENERS //
    itemSelectedHandler(itemSelected) {
        /*Remove 'active' from every item, except from the clicked one*/
        const menuItems = Array.from(this.el.querySelectorAll("gxg-menu-slim-item"));
        menuItems.forEach((menuItem) => {
            if (itemSelected.detail.ref !== menuItem) {
                menuItem.active = false;
            }
        });
        /*Hide menu*/
        if (this.hideOnSelect) {
            this.hidden = true;
        }
    }
    keyboardNavigationHandler(triggeredItem) {
        const triggeringItem = triggeredItem.detail.ref;
        const triggeringItemIndex = this._enabledItems.findIndex((item) => {
            return item === triggeringItem;
        });
        let newFocusedItem;
        if (triggeringItemIndex !== -1 &&
            triggeredItem.detail.key === KK.ARROW_UP) {
            const prevItem = this._enabledItems[triggeringItemIndex - 1];
            if (prevItem?.active && prevItem.previousElementSibling) {
                newFocusedItem = prevItem.previousElementSibling;
            }
            else if (prevItem) {
                newFocusedItem = prevItem;
            }
        }
        else if (triggeringItemIndex !== -1 &&
            triggeredItem.detail.key === KK.ARROW_DOWN) {
            const nextItem = this._enabledItems[triggeringItemIndex + 1];
            if (nextItem?.active && nextItem.nextElementSibling) {
                newFocusedItem = nextItem.nextElementSibling;
            }
            else if (nextItem) {
                newFocusedItem = nextItem;
            }
        }
        if (newFocusedItem) {
            newFocusedItem.focus();
        }
    }
    // 8.PUBLIC METHODS API //
    // 9.LOCAL METHODS //
    setFocusFirstItem = () => {
        const firstItem = this.el.querySelector("gxg-menu-slim-item");
        firstItem.focus();
    };
    assignEllipsis = () => {
        if (this.ellipsis) {
            const items = this.el.querySelectorAll("gxg-menu-slim-item");
            items &&
                items.forEach((item) => {
                    item.ellipsis = true;
                });
        }
    };
    /*Mouse out handlers*/
    attachMouseOut = () => {
        this.el.addEventListener("mouseout", this.detectMouseOut);
    };
    removeMouseOut = () => {
        this.el.removeEventListener("mouseout", this.detectMouseOut);
    };
    detectMouseOut = (e) => {
        const nodeName = e.relatedTarget.nodeName;
        if (nodeName !== "GXG-MENU" &&
            nodeName !== "GXG-MENU-LIST" &&
            nodeName !== "GXG-MENU-slim-ITEM") {
            //Mouse out
            this.startMouseOutTimeout();
        }
    };
    /*Mouse enter handlers*/
    attachMouseEnter = () => {
        this.el.addEventListener("mouseenter", this.detectMouseEnter);
    };
    removeMouseEnter = () => {
        this.el.removeEventListener("mouseenter", this.detectMouseEnter);
    };
    detectMouseEnter = () => {
        //Mouse enter
        this.clearMouseOutTimeout();
    };
    /**
     * @description
     */
    startMouseOutTimeout() {
        this.mouseOutTimeout = setTimeout(() => {
            this.hidden = true;
        }, this._mouseOutDelay);
    }
    clearMouseOutTimeout() {
        clearTimeout(this.mouseOutTimeout);
    }
    /*Detect click outside*/
    attachDetectClickOutside = () => {
        document.addEventListener("click", this.detectClickOutside);
    };
    removeDetectClickOutside = () => {
        document.removeEventListener("click", this.detectClickOutside);
    };
    /*Detect click outside*/
    detectClickOutside = (e) => {
        const clickedOutside = detectClickOutside(e, this.el);
        if (clickedOutside) {
            this.hidden = true;
        }
        else {
            this.hidden = false;
        }
    };
    /**
     * @description It renders the menu title, which is optional.
     */
    renderTitle() {
        return this.menuTitle ? (h("header", { class: "menu__header", part: "header" }, h("h1", { class: "menu__header__title", part: "title" }, this.menuTitle))) : null;
    }
    getEnabledItems = () => {
        const enabledItems = this.el.querySelectorAll("gxg-menu-slim-item");
        enabledItems.forEach((menuItem) => {
            if (!menuItem.disabled) {
                this._enabledItems.push(menuItem);
            }
        });
    };
    // 10.RENDER() FUNCTION //
    render() {
        return (h(Host, { tabindex: "-1" }, h("div", { class: {
                "outer-wrapper": true,
            }, style: {
                height: this._menuHeight,
                transitionDuration: `${this._transitionDuration}ms`,
            }, part: "outer-wrapper" }, h("div", { class: "inner-wrapper", ref: (el) => (this.innerWrapper = el), part: "inner-wrapper" }, this.renderTitle(), h("div", { class: "lists-wrapper", part: "lists-wrapper" }, h("slot", null))))));
    }
    static get is() { return "gxg-menu-slim"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["menu.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["menu.css"]
        };
    }
    static get properties() {
        return {
            "menuTitle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The menu title"
                },
                "attribute": "menu-title",
                "reflect": false
            },
            "tabs": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Provide this attribute if you are using this menu on the tabs component"
                },
                "attribute": "tabs",
                "reflect": true
            },
            "hidden": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Hides or show the menu with an animation"
                },
                "attribute": "hidden",
                "reflect": false,
                "defaultValue": "true"
            },
            "hideOnSelect": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Hides the menu when an item is selected."
                },
                "attribute": "hide-on-select",
                "reflect": false,
                "defaultValue": "true"
            },
            "ellipsis": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Prevents the menu-item's text from wrapping into more than one line, adding an ellipsis at the end."
                },
                "attribute": "ellipsis",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "hidden",
                "methodName": "hiddenHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "itemSelected",
                "method": "itemSelectedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "keyboardNavigation",
                "method": "keyboardNavigationHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=menu.js.map
